import { useLayoutEffect, useState } from 'react';

import { Modal, ModalContent } from '@braskai/ui-kit';

import type { CSSProperties, ReactNode } from 'react';

import { Box } from '@mui/material';

import { useInterfaceLanguage } from '@/shared/i18n';
import { HubSpotMeetings } from '@/shared/thirdParty/hubSpot';
import { ModalHeaderDefault } from '@/shared/ui/modal';

const HUBSPOT_EMBED_VIEWPORT_HEIGHT = 715;
const FULL_SCREEN_TITLE_SX = {
  fontSize: { mobile: '24px', tablet: '32px' },
  fontWeight: 700,
  lineHeight: { mobile: '30px', tablet: '40px' },
} as const;
const FULL_SCREEN_SUBTITLE_SX = {
  display: 'block',
  fontSize: { mobile: '16px', tablet: '18px' },
  fontWeight: 400,
  lineHeight: { mobile: '21.333px', tablet: '24px' },
  maxWidth: 'calc(100vw - 80px)',
  width: '600px',
} as const;

const PAPER_SX = {
  centered: { height: 'auto', maxHeight: 'calc(100dvh - 80px)', mx: '12px', my: '40px' },
  default: { height: 'calc(100dvh - 24px)', m: '12px', maxHeight: 'calc(100dvh - 24px)' },
  fullScreen: {
    '&&': {
      borderRadius: 0,
      height: '100dvh',
      m: 0,
      maxHeight: '100dvh',
      maxWidth: 'none',
      width: '100%',
    },
  },
} as const;

type DemoBookingCalendarModalProps = {
  title?: ReactNode;
  email?: string;
  layout?: keyof typeof PAPER_SX;
  meetingUrl: string;
  secondaryAction?: ReactNode;
  subtitle?: ReactNode;
  onBooked: (startsAt?: Date) => void;
  onClose: () => void;
  onLoadError?: () => void;
};

type IframeFitStyle = {
  '--demo-booking-iframe-height'?: string;
  '--demo-booking-iframe-scale'?: string;
  '--demo-booking-iframe-width'?: string;
} & CSSProperties;

export const DemoBookingCalendarModal = ({
  title,
  email,
  layout = 'default',
  meetingUrl,
  secondaryAction,
  subtitle,
  onBooked,
  onClose,
  onLoadError,
}: DemoBookingCalendarModalProps) => {
  const { t } = useInterfaceLanguage();
  const resolvedTitle = title ?? t('trial.booking.calendarTitle');
  const resolvedSubtitle = subtitle ?? t('trial.booking.calendarSubtitle');
  const isCentered = layout !== 'default';
  const isFullScreen = layout === 'fullScreen';
  const [calendarContainer, setCalendarContainer] = useState<HTMLDivElement | null>(null);
  const [iframeFitStyle, setIframeFitStyle] = useState<IframeFitStyle>({});

  useLayoutEffect(() => {
    if (!isFullScreen || typeof ResizeObserver === 'undefined') {
      return;
    }

    if (!calendarContainer) {
      return;
    }

    const fitIframe = () => {
      const availableHeight = calendarContainer.clientHeight;
      if (!availableHeight) {
        return;
      }

      const scale = Math.min(1, availableHeight / HUBSPOT_EMBED_VIEWPORT_HEIGHT);
      setIframeFitStyle({
        '--demo-booking-iframe-height': `${availableHeight / scale}px`,
        '--demo-booking-iframe-scale': String(scale),
        '--demo-booking-iframe-width': `${100 / scale}%`,
      });
    };

    const resizeObserver = new ResizeObserver(fitIframe);
    resizeObserver.observe(calendarContainer);
    fitIframe();

    return () => resizeObserver.disconnect();
  }, [calendarContainer, isFullScreen]);

  return (
    <Modal
      PaperProps={{
        'data-testid': isFullScreen ? 'demo-booking-calendar-page' : undefined,
        sx: PAPER_SX[layout],
      }}
      fullScreen={isFullScreen}
      size="lg"
      // Must clear the raw MUI modals it can be opened from (trial paywall sits on zIndex.modal).
      sx={{ overflow: isFullScreen ? 'hidden' : undefined, zIndex: (theme) => theme.zIndex.modal + 1 }}
      open
      onClose={onClose}
    >
      <ModalHeaderDefault
        title={
          isFullScreen ? (
            <Box component="span" sx={FULL_SCREEN_TITLE_SX}>
              {resolvedTitle}
            </Box>
          ) : (
            resolvedTitle
          )
        }
        subtitle={
          isFullScreen ? (
            <Box component="span" sx={FULL_SCREEN_SUBTITLE_SX}>
              {resolvedSubtitle}
            </Box>
          ) : (
            resolvedSubtitle
          )
        }
        align={isCentered ? 'center' : 'left'}
        gap={isCentered ? 8 : undefined}
        onClose={onClose}
      />

      <ModalContent
        sx={{
          '& .meetings-iframe-container': {
            height: isFullScreen ? '100%' : undefined,
            overflow: isFullScreen ? 'hidden' : undefined,
            width: '100%',
          },
          '& .meetings-iframe-container iframe': {
            display: 'block',
            height: isFullScreen ? 'var(--demo-booking-iframe-height, 100%) !important' : undefined,
            transform: isFullScreen ? 'scale(var(--demo-booking-iframe-scale, 1))' : undefined,
            transformOrigin: isFullScreen ? 'top left' : undefined,
            width: isFullScreen ? 'var(--demo-booking-iframe-width, 100%) !important' : '100% !important',
          },
          display: isFullScreen ? 'flex' : undefined,
          flex: 1,
          minHeight: 0,
          overflow: isFullScreen ? 'hidden' : undefined,
          overflowY: isFullScreen ? undefined : 'auto',
          ...(isCentered && { '&&': { pb: isFullScreen ? '24px' : undefined, pt: 0 } }),
          ...(isFullScreen && {
            boxSizing: 'border-box',
            mx: 'auto',
            width: '100%',
          }),
        }}
      >
        <div
          style={
            isFullScreen
              ? {
                  flex: '1 1 auto',
                  margin: '0 auto',
                  maxWidth: 1200,
                  minHeight: 0,
                  width: '100%',
                  ...iframeFitStyle,
                }
              : undefined
          }
          data-testid={isFullScreen ? 'demo-booking-calendar-container' : undefined}
          ref={setCalendarContainer}
        >
          <HubSpotMeetings email={email} meetingUrl={meetingUrl} onBooked={onBooked} onLoadError={onLoadError} />

          {secondaryAction}
        </div>
      </ModalContent>
    </Modal>
  );
};
